import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ChevronRight, ChevronDown, BookOpen, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import BetaFeedbackPanel from './BetaFeedbackPanel';
import { useLessonContent } from './useLessonContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SECTIONS = {
  opening_p1: 'There is an argument for a pre-tribulation rapture that does not come from a single verse — it comes from the structure of Revelation itself. The claim: the book is strictly chronological, chapter after chapter in a straight line. But Revelation is not that kind of book.',
  opening_p2: 'John wrote it the way Hebrew authors have always written — with recapitulation. Once you see it, you cannot unsee it.',
  what_is_recap_text: "Recapitulation means the author retells the same period from a different angle. Think of three cameras filming the same event from three positions. Each one captures the whole thing — but from a different perspective. That is what the Seals, the Trumpets, and the Bowls are: three camera angles on the same period — not three sequential series stacked end to end.",
  diagram_note: 'All three series terminate at the same event. Not three different endpoints — one.',
  rules_out_text: "The pretrib structural argument requires strict linear sequence — the church is raptured before the seals, then the seals happen, then the trumpets, then the bowls, all in order. Recapitulation eliminates it. If the three series cover the same period from different angles, then there is no gap before the seals for a rapture to have already occurred. The gathering happens where the text says it does — at the last trumpet.",
  git_who: "John, writing what he was shown — three panoramic views of the same tribulation period.",
  git_what: "Seals, Trumpets, and Bowls that converge at the same terminus — not three sequential timelines.",
  git_when: "All three seventh positions arrive at the same moment. The Euphrates events match at position six. Christ's 'thief' warning appears during the bowls — the gathering has not yet occurred.",
  appendix_code: 'APP-BM',
  appendix_title: 'Appendix BM: The Architecture of Revelation',
  appendix_desc: 'A complete structural layout of the book of Revelation showing how the three series interlock.',
  bridge_summary_git: 'The GIT — your reading tool.',
  bridge_summary_bp: "The Blueprints — God's appointed calendar.",
  bridge_summary_recap: 'Recapitulation — the structure of Revelation.',
  bridge_next: 'Open 2 Thessalonians 2. The first corner piece.',
};

const DEFAULT_ARRAYS = {
  convergence_points: [
    { title: 'The Seventh Position', desc: "The 7th Seal, the 7th Trumpet, and the 7th Bowl all terminate at the same event — the consummation. If these were strictly sequential, each series would end at a different time. They don't. All three seventh positions arrive at the same destination.", refs: 'Revelation 8:1, Revelation 11:15–18, Revelation 16:17–21' },
    { title: 'The Sixth-Position Euphrates', desc: 'In the 6th Trumpet, armies are released at the Euphrates. In the 6th Bowl, the Euphrates is dried up to make way for the kings of the East. Same river. Same armies. Two descriptions — not two events.', refs: 'Revelation 9:13–16, Revelation 16:12–16' },
    { title: 'Revelation 16:15 — The Intrusion', desc: '"Behold, I come as a thief." This is spoken in the middle of the bowls — during the 6th Bowl. If the rapture already happened before the seals, why is Jesus still warning about coming as a thief during the bowls?', refs: 'Revelation 16:15, 1 Thessalonians 5:2' },
  ],
};

export default function RecapitulationLesson() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const theme = useTheme();
  const [completing, setCompleting] = useState(false);
  const [revealed, setRevealed] = useState({});
  const isAdmin = user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com';
  const { c, arr } = useLessonContent('RECAPITULATION', DEFAULT_SECTIONS, DEFAULT_ARRAYS);

  const toggle = (key) => setRevealed(prev => ({ ...prev, [key]: !prev[key] }));

  const handleComplete = useCallback(async () => {
    if (!token && !user) {
      toast.info('Sign in to save your progress and continue');
      sessionStorage.setItem('pendingLessonCompletion', JSON.stringify({ moduleKey: 'RECAPITULATION', nextRoute: '/' }));
      navigate('/login', { state: { from: { pathname: '/lesson/recapitulation' } } });
      return;
    }
    setCompleting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/learning/progress/RECAPITULATION/start`, { method: 'POST', headers, credentials: 'include' });
      const res = await fetch(`${API_URL}/api/learning/progress/RECAPITULATION/complete`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        toast.success('All three Legend Keys complete! The Corner Pieces are now unlocked.');
        setTimeout(() => navigate('/'), 1500);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Could not complete module');
      }
    } catch { toast.error('Error completing module'); }
    setCompleting(false);
  }, [token, user, navigate]);

  // Auto-complete if returning from login with a pending action
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingLessonCompletion');
    if (pending && (token || user)) {
      try {
        const { moduleKey, nextRoute } = JSON.parse(pending);
        if (moduleKey === 'RECAPITULATION') {
          sessionStorage.removeItem('pendingLessonCompletion');
          (async () => {
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            await fetch(`${API_URL}/api/learning/progress/RECAPITULATION/start`, { method: 'POST', headers, credentials: 'include' });
            const res = await fetch(`${API_URL}/api/learning/progress/RECAPITULATION/complete`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) {
              toast.success('All three Legend Keys complete! The Corner Pieces are now unlocked.');
              setTimeout(() => navigate(nextRoute), 1500);
            }
          })();
        }
      } catch {}
    }
  }, [token, user, navigate]);

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, fontFamily: "'Times New Roman', 'Georgia', serif" }}>
      <nav className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: theme.borderPrimary }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm transition-colors" style={{ color: theme.navText }} data-testid="back-to-map">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to the Map
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Legend Key 3</span>
        {isAdmin ? (
          <button onClick={() => navigate('/admin/lesson-content/RECAPITULATION')} className="flex items-center gap-1.5 text-xs" style={{ color: theme.accentPrimary }} data-testid="btn-edit-content">
            <PenTool className="w-3.5 h-3.5" /> Edit Content
          </button>
        ) : <div className="w-20" />}
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-12">

        {/* Title */}
        <div className="text-center mb-12" data-testid="lesson-header">
          <p className="text-xs tracking-[0.5em] uppercase mb-6" style={{ color: theme.labelColor }}>RECAPITULATION — HOW REVELATION IS WRITTEN</p>
          <p className="text-sm italic max-w-xl mx-auto leading-relaxed" style={{ color: theme.textSecondary }}>
            The Scale — structural proportions
          </p>
        </div>

        {/* Opening Teaching */}
        <div className="mb-12" data-testid="opening-teaching">
          <p className="text-base leading-relaxed mb-6" style={{ color: theme.textPrimary }}>
            {c('opening_p1')}
          </p>
          <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>
            {c('opening_p2')}
          </p>
        </div>

        {/* What is Recapitulation? */}
        <div className="mb-12" data-testid="what-is-recapitulation">
          <h2 className="text-lg font-bold mb-4 tracking-wide" style={{ color: theme.labelColor }}>WHAT IS RECAPITULATION?</h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: theme.textPrimary }}>
            {c('what_is_recap_text')}
          </p>

          {/* Diagram */}
          <div className="rounded-xl border p-6 text-center" style={{ background: theme.scriptureBg, borderColor: theme.scriptureBorder }}>
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.labelColor }}>THE STRUCTURE</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: theme.accentBg, border: `1px solid ${theme.accentBorder}` }}>
                <span className="text-sm font-bold" style={{ color: theme.accentPrimary }}>7 Seals</span>
                <div className="flex-1 mx-4 h-px" style={{ background: `${theme.accentPrimary}33` }} />
                <span className="text-xs" style={{ color: theme.accentPrimary }}>→ 7th Seal = Consummation</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: theme.accentBg, border: `1px solid ${theme.accentBorder}` }}>
                <span className="text-sm font-bold" style={{ color: theme.accentPrimary }}>7 Trumpets</span>
                <div className="flex-1 mx-4 h-px" style={{ background: `${theme.accentPrimary}33` }} />
                <span className="text-xs" style={{ color: theme.accentPrimary }}>→ 7th Trumpet = Consummation</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: theme.accentBg, border: `1px solid ${theme.accentBorder}` }}>
                <span className="text-sm font-bold" style={{ color: theme.accentPrimary }}>7 Bowls</span>
                <div className="flex-1 mx-4 h-px" style={{ background: `${theme.accentPrimary}33` }} />
                <span className="text-xs" style={{ color: theme.accentPrimary }}>→ 7th Bowl = Consummation</span>
              </div>
            </div>
            <p className="mt-4 text-xs italic" style={{ color: theme.textSecondary }}>
              {c('diagram_note')}
            </p>
          </div>
        </div>

        {/* Convergence Points — Interactive */}
        <div className="mb-12" data-testid="convergence-points">
          <h2 className="text-lg font-bold mb-6 tracking-wide" style={{ color: theme.labelColor }}>THE EVIDENCE: THREE CONVERGENCE POINTS</h2>
          <div className="space-y-4">
            {arr('convergence_points').map((point, i) => {
              const refsArray = typeof point.refs === 'string' ? point.refs.split(', ') : (point.refs || []);
              return (
              <button key={i} onClick={() => toggle(i)}
                className="w-full text-left rounded-xl border p-5 transition-all"
                style={{
                  background: revealed[i] ? theme.scriptureBg : theme.bgCard,
                  borderColor: revealed[i] ? theme.scriptureBorder : theme.borderPrimary,
                }}
                data-testid={`convergence-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-base font-bold" style={{ color: theme.accentPrimary }}>Convergence {i + 1}: {point.title}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${revealed[i] ? 'rotate-180' : ''}`} style={{ color: theme.navText }} />
                </div>
                {revealed[i] && (
                  <div className="mt-3">
                    <p className="text-sm leading-relaxed mb-3" style={{ color: theme.textPrimary }}>{point.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {refsArray.map((ref, j) => (
                        <span key={j} className="text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(30,58,95,0.15)', color: theme.scriptureText, border: '1px solid rgba(30,58,95,0.2)' }}>{ref}</span>
                      ))}
                    </div>
                  </div>
                )}
              </button>
              );
            })}
          </div>
        </div>

        {/* What This Rules Out */}
        <div className="mb-12 rounded-xl border p-6" style={{ background: 'rgba(220,38,38,0.03)', borderColor: 'rgba(220,38,38,0.15)' }} data-testid="rules-out">
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-bold" style={{ color: theme.labelColor }}>WHAT THIS RULES OUT</p>
          <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>
            {c('rules_out_text')}
          </p>
        </div>

        {/* GIT Application */}
        <div className="mb-12" data-testid="git-application">
          <h2 className="text-lg font-bold mb-4 tracking-wide" style={{ color: theme.labelColor }}>APPLY THE GIT</h2>
          <div className="rounded-xl border p-5" style={{ background: theme.scriptureBg, borderColor: theme.scriptureBorder }}>
            <p className="text-sm leading-relaxed mb-3" style={{ color: theme.textSecondary }}>
              <strong style={{ color: theme.accentPrimary }}>WHO?</strong> — {c('git_who')}
            </p>
            <p className="text-sm leading-relaxed mb-3" style={{ color: theme.textSecondary }}>
              <strong style={{ color: theme.accentPrimary }}>WHAT?</strong> — {c('git_what')}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
              <strong style={{ color: theme.accentPrimary }}>WHEN?</strong> — {c('git_when')}
            </p>
          </div>
        </div>

        {/* Bridge to Appendix */}
        <div className="mb-12 rounded-xl border p-5" style={{ background: theme.bgCardHover, borderColor: theme.borderPrimary }} data-testid="appendix-link">
          <p className="text-xs tracking-widest uppercase mb-2" style={{ color: theme.textMuted }}>DIG DEEPER</p>
          <p className="text-sm font-bold mb-1" style={{ color: theme.accentPrimary }}>{c('appendix_title')}</p>
          <p className="text-xs leading-relaxed" style={{ color: theme.textSecondary }}>
            {c('appendix_desc')}
          </p>
        </div>

        {/* Beta Feedback */}
        <BetaFeedbackPanel moduleKey="RECAPITULATION" />

        {/* Bridge CTA */}
        <div className="text-center py-8" data-testid="bridge-cta">
          <div className="h-px mx-auto mb-8" style={{ maxWidth: '200px', background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
          <p className="text-base mb-3 italic" style={{ color: theme.textSecondary }}>
            You now have all three Legend Keys.
          </p>
          <p className="text-sm mb-3" style={{ color: theme.textSecondary }}>
            {c('bridge_summary_git')}
          </p>
          <p className="text-sm mb-3" style={{ color: theme.textSecondary }}>
            {c('bridge_summary_bp')}
          </p>
          <p className="text-sm mb-6" style={{ color: theme.textSecondary }}>
            {c('bridge_summary_recap')}
          </p>
          <p className="text-lg font-bold mb-8" style={{ color: theme.accentPrimary }}>
            {c('bridge_next')}
          </p>
          <button onClick={handleComplete} disabled={completing}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base font-bold transition-all border"
            style={{ background: `linear-gradient(135deg, ${theme.accentPrimary}18, ${theme.accentSecondary}18)`, borderColor: `${theme.accentPrimary}66`, color: theme.accentPrimary }}
            data-testid="btn-complete-recapitulation">
            <BookOpen className="w-5 h-5" />
            {completing ? 'Marking Complete...' : 'I Have All Three Keys — Open the Corners'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
